'use client';

import { useState, type MouseEvent } from 'react';
import type { MobileFrontEndGalleryView } from '@/shared/mobile-service/api/cors-mobile-api';
import { storageUrlForDisplay } from '@/shared/storageUrlForDisplay';
import type { MapPoint } from '@/shared/mobile-service/lib/map-points';
import { resolvePath } from '@/shared/mobile-service/lib/message-mapping';
import { CollectedMediaPreview } from '../media/CollectedMediaPreview';
import type { CollectedMediaTarget, ResolveCollectedMediaInfo } from '../media/collected-media';
import { buildPointDetailEntries, pointAudioUrl, pointMediaTarget } from './point-popup';

function stopMapClick(event: MouseEvent) {
  event.stopPropagation();
}

function initialIndex(points: MapPoint[], initialId: string | undefined): number {
  if (!initialId) return 0;
  const index = points.findIndex((point) => point.id === initialId);
  return index >= 0 ? index : 0;
}

/**
 * Popup body for one pin, or a prev/next stack of pins at the same coordinate.
 *
 * A single point hides the controls. Media mounts only for the visible item.
 */
export function PointStackPopup({
  points,
  initialId,
  detailFields,
  mediaPreview,
  resolvePreview,
  resolvePreviewInfo,
  onOpen,
}: {
  points: MapPoint[];
  initialId?: string;
  detailFields: string[];
  mediaPreview?: MobileFrontEndGalleryView;
  resolvePreview?: (url: string) => Promise<string | null>;
  resolvePreviewInfo?: ResolveCollectedMediaInfo;
  onOpen?: (media: CollectedMediaTarget) => void;
}) {
  const [index, setIndex] = useState(() => initialIndex(points, initialId));
  const total = points.length;
  const point = points[index] ?? points[0];
  if (!point) return null;

  const step = (delta: number) => {
    setIndex((current) => (current + delta + total) % total);
  };
  const media = pointMediaTarget(point.row, mediaPreview);
  const audioUrl = media ? null : pointAudioUrl(point.row, mediaPreview);
  const details = buildPointDetailEntries(point.row, detailFields, resolvePath);

  return (
    <div className="mv-point-popup">
      {total > 1 ? (
        <div className="mv-point-nav">
          <button
            type="button"
            aria-label="Previous"
            onMouseDown={stopMapClick}
            onClick={(event) => {
              stopMapClick(event);
              step(-1);
            }}
          >
            Previous
          </button>
          <span>
            {index + 1} of {total}
          </span>
          <button
            type="button"
            aria-label="Next"
            onMouseDown={stopMapClick}
            onClick={(event) => {
              stopMapClick(event);
              step(1);
            }}
          >
            Next
          </button>
        </div>
      ) : null}
      {media ? (
        <CollectedMediaPreview
          key={`${index}:${media.url}`}
          url={media.url}
          mime={media.mime}
          kind={media.kind}
          filename={point.label}
          compact
          resolvePreview={resolvePreview}
          resolvePreviewInfo={resolvePreviewInfo}
          onOpen={
            onOpen
              ? (previewUrl) =>
                  onOpen({
                    url: media.url,
                    kind: media.kind,
                    filename: point.label,
                    previewUrl,
                  })
              : undefined
          }
        />
      ) : null}
      {audioUrl ? (
        <audio
          className="mv-point-media"
          controls
          preload="metadata"
          src={storageUrlForDisplay(audioUrl)}
          onMouseDown={stopMapClick}
          onClick={stopMapClick}
        />
      ) : null}
      {point.label ? <div className="mv-point-title">{point.label}</div> : null}
      {point.timestamp ? <div className="mv-point-timestamp">{point.timestamp}</div> : null}
      {details.map((entry, detailIndex) => (
        <div key={`${entry.label}-${detailIndex}`} className="mv-point-row">
          <span className="mv-point-key">{entry.label}</span>
          <span className="mv-point-val">{entry.value}</span>
        </div>
      ))}
      <div className="mv-point-coords">
        {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
      </div>
    </div>
  );
}
